import {
    createContext,
    useContext,
    useEffect,
    useState,
    useRef,
    useCallback,
} from "react";
import { auth, db } from "../services/firebase";
import {
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    signOut,
    signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const IDLE_TIMEOUT = 15 * 60 * 1000;
const WARNING_TIME = 14 * 60 * 1000;

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const navigate = useNavigate();

    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(60);

    const hasRedirected = useRef(false);

    const idleTimer = useRef(null);
    const warningTimer = useRef(null);
    const countdownTimer = useRef(null);

    /* ================= AUTH ================= */

    async function signup(name, email, phone, password) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const profileData = {
            name,
            email,
            phone,
            organization: "Carolina Sports Club",
            color: "#00ff88",
            role: "member",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        await setDoc(doc(db, "csc_users", user.uid), profileData);
        setUserProfile(profileData);

        return user;
    }

    async function login(email, password) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        const snap = await getDoc(doc(db, "csc_users", user.uid));
        if (snap.exists()) setUserProfile(snap.data());

        hasRedirected.current = false;
        return user;
    }

    const logout = useCallback(() => {
        setUserProfile(null);
        setShowWarning(false);

        clearTimeout(idleTimer.current);
        clearTimeout(warningTimer.current);
        clearInterval(countdownTimer.current);

        // CLEAR CART
        localStorage.removeItem("csc_cart");

        localStorage.setItem("logout", Date.now());

        if (!hasRedirected.current) {
            hasRedirected.current = true;
            navigate("/signin");
        }

        return signOut(auth);
    }, [navigate]);

    /* ================= COUNTDOWN ================= */

    const startCountdown = useCallback(() => {
        clearInterval(countdownTimer.current);

        setShowWarning(true);
        setCountdown(60);

        countdownTimer.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownTimer.current);
                    logout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [logout]);

    /* ================= TIMER SYSTEM ================= */

    const startTimers = useCallback(() => {
        clearTimeout(idleTimer.current);
        clearTimeout(warningTimer.current);
        clearInterval(countdownTimer.current);

        setShowWarning(false);

        if (!currentUser) return;

        warningTimer.current = setTimeout(() => {
            startCountdown();
        }, WARNING_TIME);

        idleTimer.current = setTimeout(() => {
            logout();
        }, IDLE_TIMEOUT);
    }, [currentUser, logout, startCountdown]);

    const resetTimer = useCallback(() => {
        localStorage.setItem("activity", Date.now());
        startTimers();
    }, [startTimers]);

    /* ================= ACTIVITY LISTENERS ================= */

    useEffect(() => {
        if (!currentUser) return;

        const events = ["click", "keydown", "mousedown", "touchstart"];

        events.forEach((e) => window.addEventListener(e, resetTimer));

        startTimers(); // IMPORTANT: start timer on login/load

        return () => {
            events.forEach((e) => window.removeEventListener(e, resetTimer));

            clearTimeout(idleTimer.current);
            clearTimeout(warningTimer.current);
            clearInterval(countdownTimer.current);
        };
    }, [currentUser, resetTimer, startTimers]);

    /* ================= CROSS TAB SYNC ================= */

    useEffect(() => {
        const handleStorage = (event) => {
            if (event.key === "logout") {
                signOut(auth);
            }

            if (event.key === "activity") {
                resetTimer();
            }
        };

        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [resetTimer]);

    /* ================= AUTH STATE ================= */

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);

            if (user) {
                const snap = await getDoc(doc(db, "csc_users", user.uid));
                if (snap.exists()) setUserProfile(snap.data());
            } else {
                setUserProfile(null);
            }

            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        userProfile,
        isAuthenticated: !!currentUser,
        signup,
        login,
        logout,
    };

    if (loading) return null;

    return (
        <AuthContext.Provider value={value}>
            {children}

            {showWarning && (
                <div style={styles.overlay}>
                    <div style={styles.modal}>
                        <h3>Session Expiring</h3>
                        <p>You’ll be logged out due to inactivity</p>

                        <div style={styles.countdown}>{countdown}s</div>

                        <button style={styles.button} onClick={resetTimer}>
                            Stay Logged In
                        </button>
                    </div>
                </div>
            )}
        </AuthContext.Provider>
    );
}

/* ================= STYLES ================= */

const styles = {
    overlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
    },
    modal: {
        background: "#0a1a0a",
        padding: "30px",
        borderRadius: "18px",
        textAlign: "center",
        color: "#fff",
        border: "1px solid rgba(0,255,136,0.3)",
        minWidth: "280px",
    },
    countdown: {
        fontSize: "36px",
        fontWeight: "bold",
        margin: "15px 0",
        color: "#00ff88",
    },
    button: {
        padding: "10px 20px",
        borderRadius: "25px",
        border: "none",
        background: "linear-gradient(90deg,#00ff88,#00d4ff)",
        cursor: "pointer",
        fontWeight: "bold",
    },
};